import {
  faFile,
  faFilePdf,
  faPaperclip,
  faPhotoFilm,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResultOf } from '@graphql-typed-document-node/core';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FragmentType, getFragmentData, graphql } from '~/gql';
import { Attachment } from '~/gql/graphql';
import { parseJson } from '~/helpers';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { Contact } from '~/routes/resources/contacts';
import { FindAddContact } from '../FindAddContact';
import { TextArea } from '../form/textarea/TextArea';
import { Input } from '../form/TextField';
import { SelectCombolist } from '../SelectCombolist';
import { Label } from '../ui/Label';
import { Upload, UploadResult, toAttachmentInput } from '../upload2';
import { loadPreviewsFromStorage } from '../upload2/Upload';
import { AttributeReadValue } from './AttributeReadValue';

const Attribute_AttributeFragment = graphql(`
  fragment Attribute_AttributeFragment on Attribute {
    id
    type
    category
    name
    value
    condition
    options
    attachments {
      ...Attribute_AttachmentFragment
    }
  }
`);

// TODO remove this export and instead unmask the fragment exactly where it is needed
export type Attribute = ResultOf<typeof Attribute_AttributeFragment>;

type Props = {
  attribute: FragmentType<typeof Attribute_AttributeFragment>;
  value: string;
  attachments?: Attachment[];
  onChange: (value: string) => void;
  readMode?: boolean;
  contacts: Contact[];
};

export const byId =
  (search: string) =>
  (attribute: FragmentType<typeof Attribute_AttributeFragment>) => {
    const { id } = getFragmentData(Attribute_AttributeFragment, attribute);

    return id === search;
  };

export function parseCondition(
  attribute: FragmentType<typeof Attribute_AttributeFragment>
) {
  const { id, condition } = getFragmentData(
    Attribute_AttributeFragment,
    attribute
  );

  if (!condition) {
    return null;
  }

  const cond = condition.split('===');

  if (cond.length !== 2) {
    console.warn('Invalid condition for attribute', id, condition);
    return null;
  }

  return cond as [id: string, eq: string];
}

export function AttributeInput(props: Props) {
  const attribute = getFragmentData(
    Attribute_AttributeFragment,
    props.attribute
  );
  const { value, onChange, attachments, contacts } = props;
  const { isMobile } = useBreakpoint();

  // used to hold upload previews
  const [previews, setPreviews] = useState<UploadResult[]>(() => {
    const old = parseJson(value);
    return old && Array.isArray(old)
      ? old
          // .filter(({ kind }: { kind: string }) => kind === 'image')
          .map((a: { id: string; kind: string; originalFilename: string }) => ({
            id: a.id,
            preview: attachments?.find(({ id }) => id === a.id)?.thumb ?? '',
            name: a.originalFilename,
            kind: a.kind as 'image' | 'file',
          }))
      : [];
  });

  // mix in any previews found in offline storage as required (when not in attachments)
  useEffect(() => {
    async function restorePreviews() {
      const offlineImages = await loadPreviewsFromStorage();
      const parsedValue = parseJson(value);
      if (parsedValue && Array.isArray(parsedValue)) {
        setPreviews((prev) => {
          const missing = previews.filter(({ preview }) => !preview);
          const newPreviews = [...prev];

          missing.forEach((m) => {
            const offline = offlineImages.find(({ id }) => id === m.id);

            if (offline) {
              console.log('found missing', m.id, 'in offline storage', offline);
              const idx = newPreviews.findIndex(({ id }) => id === offline.id);

              if (idx > -1) {
                newPreviews[idx].preview = URL.createObjectURL(offline.data);
              }
            }
          });

          return newPreviews;
        });
      }
    }
    restorePreviews();
  }, []);

  // When previews (field value) changes, trigger onChange
  // useEffect(() => {
  //   // ...
  // }, [previews]);

  const handleUploaded = (uploaded: UploadResult[]) => {
    setPreviews((state) => {
      const newPreviews = [...state, ...uploaded];

      // ! Seems bad to be firing onChange from within the state setter
      // consider using the useEffect commented out just above
      const value = JSON.stringify(newPreviews.map(toAttachmentInput));
      onChange(value);

      return newPreviews;
    });
  };

  const label = attribute.name;

  if (props.readMode) {
    return (
      <div className='relative mb-5 flex flex-col gap-1'>
        <Label>{label}</Label>
        <div className='px-1'>
          <AttributeReadValue
            value={value}
            attribute={attribute}
            attachments={attachments}
            contacts={contacts}
          />
        </div>
      </div>
    );
  }

  if (attribute.type === 'date' || attribute.type === 'number') {
    return (
      <Input
        type={attribute.type}
        label={label}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    );
  }

  if (attribute.type === 'contact') {
    const oldValue = parseJson(value);
    return (
      <FindAddContact
        optionsBoxHeight={
          isMobile ? 'max-h-[calc(100vh-285px)]' : 'max-h-[calc(100vh-450px)]'
        }
        label={label}
        value={
          Array.isArray(oldValue)
            ? contacts.filter(({ id }) => oldValue.includes(id))
            : []
        }
        onChange={(selected) =>
          onChange(
            selected.length
              ? JSON.stringify(selected.map(({ id }) => id))
              : JSON.stringify([])
          )
        }
        multiple
        isAbsolute={false}
        contacts={contacts}
      />
    );
  }

  if (attribute.type === 'attachment') {
    return (
      <div className='mb-5 flex flex-col items-start gap-2'>
        <Label>{label}</Label>

        {previews.length > 0 && (
          <div className='mb-2 mt-2 grid w-full grid-cols-2 gap-4 xl:grid-cols-4'>
            {previews.map(({ id, kind, preview, name }) => (
              <div key={id} className='relative'>
                <button
                  type='button'
                  className='absolute -right-3.5 -top-2.5 z-10 h-8 w-8 rounded-full bg-brand text-xl text-white shadow'
                  onClick={() => {
                    const oldValue = parseJson(value);
                    const filtered = Array.isArray(oldValue)
                      ? oldValue.filter((v) => v.id !== id)
                      : [];
                    setPreviews((prev) => prev.filter((p) => p.id !== id));
                    // Only stringify if there are remaining items, otherwise send empty string
                    onChange(
                      filtered.length > 0
                        ? JSON.stringify(filtered)
                        : JSON.stringify([])
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
                <div className='relative flex h-[130px] flex-col items-center justify-center gap-2 overflow-hidden rounded-xl bg-shade-10'>
                  {kind === 'image' ? (
                    <img
                      key={id}
                      className='absolute inset-0 block h-full w-full object-contain'
                      alt={name || 'Thumbnail'}
                      src={preview}
                    />
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={name?.endsWith('.pdf') ? faFilePdf : faFile}
                        className='h-10 w-10 text-primary'
                      />
                      <span
                        className={classNames(
                          'text-center leading-5',
                          !name && 'italic'
                        )}
                      >
                        {name ?? 'Unknown'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {attribute.id && (
          <Upload
            id={`AttributeInput:${attribute.id}`}
            imagesOnly={attribute.options?.kind !== 'file'}
            onUploaded={handleUploaded}
          >
            <div className='ease-standard mx-1 my-2 ml-0 flex h-8 w-fit cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border border-outline-variant bg-white px-4 text-base font-normal text-secondary transition-all hover:border-outline-default hover:text-primary active:border-outline-active active:text-primary'>
              {attribute.options?.kind !== 'file' ? (
                <>
                  <FontAwesomeIcon
                    icon={faPhotoFilm}
                    className='text-primary'
                  />{' '}
                  Add Photo/s
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faPaperclip}
                    className='text-primary'
                  />{' '}
                  Add Files
                </>
              )}
            </div>
          </Upload>
        )}
      </div>
    );
  }

  if (attribute.type === 'select') {
    return (
      <SelectCombolist
        label={label}
        value={value}
        options={attribute.value.map((value) => ({ value, label: value }))}
        onChange={(v) => onChange(v ?? '')}
        searchable={false}
      />
    );
  }

  return (
    <>
      <TextArea
        data-attribute={attribute.id}
        label={label}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        className='mb-5'
        rows={1}
      />
    </>
  );
}
